import moment from "moment-timezone";

export const ADMIN_PERIODS = [
  { id: "7d", label: "7 дней", days: 7 },
  { id: "30d", label: "30 дней", days: 30 },
  { id: "all", label: "Всё время", days: null },
];

export const ADMIN_QUEUES = [
  { id: "all", label: "Все пользователи" },
  { id: "active", label: "Автосписание активно" },
  { id: "expiring", label: "Продление в 7 дней" },
  { id: "failed", label: "Проблема со списанием" },
  { id: "cancelled", label: "Отменили, доступ есть" },
  { id: "unpaid", label: "Тариф без доступа" },
  { id: "noTariff", label: "Без тарифа" },
  { id: "referred", label: "По рефералке" },
  { id: "referredPaid", label: "Реферал и оплатил" },
];

// Текущие цены из экранов тарифов. Годовой платёж нормализуем до месяца,
// чтобы MRR можно было сравнивать с месячным тарифом.
const MONTHLY_PRICE_RUBLES = 1500;
const YEARLY_PRICE_PER_MONTH_RUBLES = 12000 / 12;

const hasValue = (value) =>
  value !== null && value !== undefined && value !== "";

const hasTariff = (user) => user.tariff === "m" || user.tariff === "y";
const hasRebill = (user) => hasValue(user.rebillid);
const hasReferral = (user) => hasValue(user.referred_by);

const hasAccess = (user, now, timeZone) =>
  hasValue(user.paid_until) &&
  moment.utc(user.paid_until).tz(timeZone).isSameOrAfter(now);

const paymentMoment = (payment) => {
  const value =
    payment?.payment_date || payment?.created_at || payment?.date || null;
  const parsed = value ? moment.utc(value) : null;
  return parsed?.isValid() ? parsed : null;
};

export const isConfirmedPayment = (payment) =>
  String(payment?.status || "").toUpperCase() === "CONFIRMED";

export const isRejectedPayment = (payment) =>
  String(payment?.status || "").toUpperCase() === "REJECTED";

// Тинькофф: AUTHORIZED — деньги уже захолдированы, кабинет считает оплату
// успешной. CONFIRMED — списание. Вебхук Confirm иногда не доходит (повтор
// после REJECTED), поэтому в кассу входят оба статуса.
export const isSuccessfulCharge = (payment) => {
  const status = String(payment?.status || "").toUpperCase();
  return status === "CONFIRMED" || status === "AUTHORIZED";
};

const paymentGroupKey = (payment) =>
  String(payment?.payment_id || payment?.paymentId || payment?.id || "");

const pickLatestPayment = (left, right) => {
  const leftTime = left?.dateValue || paymentMoment(left)?.valueOf() || 0;
  const rightTime = right?.dateValue || paymentMoment(right)?.valueOf() || 0;
  return rightTime >= leftTime ? right : left;
};

/** Один payment_id = одна оплата, даже если вебхуки пришли pending → AUTHORIZED → CONFIRMED. */
export const uniqueLatestPayments = (payments) => {
  const latest = new Map();
  payments.forEach((payment) => {
    const key = paymentGroupKey(payment);
    if (!key) {
      latest.set(`row-${latest.size}-${payment.user_id}`, payment);
      return;
    }
    const current = latest.get(key);
    latest.set(key, current ? pickLatestPayment(current, payment) : payment);
  });
  return [...latest.values()];
};

// Как в бонусах рефералки: пробный платёж ~1 ₽ не считается оплатой тарифа.
const TARIFF_PAYMENT_KOPECKS = 10000;

const hasPaidTariff = (user) =>
  uniqueLatestPayments(user.payments || []).some(
    (payment) =>
      isSuccessfulCharge(payment) &&
      paymentAmountKopecks(payment) > TARIFF_PAYMENT_KOPECKS,
  );

/**
 * Делим пользователей по текущему состоянию доступа, а не по истории платежей.
 * Корзины статуса взаимоисключающие. Реферальные очереди — отдельно:
 * человек может быть и «активным», и «пришёл по коду».
 */
export const classifyUsers = (
  users,
  timeZone = "Europe/Moscow",
  now = moment().tz(timeZone),
) => {
  const groups = {
    active: [],
    cancelled: [],
    failed: [],
    unpaid: [],
    noTariff: [],
    expiring: [],
    referred: [],
    referredPaid: [],
  };

  users.forEach((user) => {
    // Реферал не конкурирует с очередями статуса: человек может быть
    // и «активным», и «пришёл по коду» одновременно.
    if (hasReferral(user)) {
      groups.referred.push(user);
      if (hasPaidTariff(user)) {
        groups.referredPaid.push(user);
      }
    }

    const tariffSelected = hasTariff(user);
    const rebillEnabled = hasRebill(user);
    const accessActive = hasAccess(user, now, timeZone);
    if (tariffSelected && rebillEnabled && accessActive) {
      groups.active.push(user);

      const paidUntil = moment.utc(user.paid_until).tz(timeZone);
      if (
        paidUntil.isSameOrBefore(now.clone().add(7, "days")) &&
        paidUntil.isSameOrAfter(now)
      ) {
        groups.expiring.push(user);
      }
      return;
    }

    if (tariffSelected && !rebillEnabled && accessActive) {
      groups.cancelled.push(user);
      return;
    }

    if (tariffSelected && rebillEnabled && !accessActive) {
      // Это очередь на проверку, а не утверждение о причине: rebillid ещё
      // сохранён, но доступ уже закончился. REJECTED считаем отдельно.
      groups.failed.push(user);
      return;
    }

    if (tariffSelected) {
      groups.unpaid.push(user);
      return;
    }

    groups.noTariff.push(user);
  });

  return groups;
};

const countTariffs = (users) => ({
  monthly: users.filter((user) => user.tariff === "m").length,
  yearly: users.filter((user) => user.tariff === "y").length,
});

const paymentIsInPeriod = (payment, period, timeZone) => {
  if (!period?.days) return true;
  const date = paymentMoment(payment);
  return Boolean(
    date &&
      date
        .tz(timeZone)
        .isSameOrAfter(moment().tz(timeZone).subtract(period.days, "days")),
  );
};

export const MONTH_LABELS = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];

export const collectUserPayments = (users) =>
  users.flatMap((user) =>
    (Array.isArray(user.payments) ? user.payments : []).map((payment) => ({
      ...payment,
      user_id: payment.user_id ?? user.id,
    })),
  );

export const paymentAmountKopecks = (payment) => {
  const value = Number(payment?.amount);
  return Number.isFinite(value) ? value : null;
};

export const isRefundedPayment = (payment) =>
  String(payment?.status || "").toUpperCase() === "REFUNDED";

const ADMIN_REFUND_PREFIX = "admin refund";

/** REFUNDED только после ручной пометки в админке (не пробный возврат из Tinkoff). */
export const isAdminMarkedRefund = (payment) => {
  const description = String(payment?.description || "");
  return (
    isRefundedPayment(payment) &&
    (description === ADMIN_REFUND_PREFIX ||
      description.startsWith(`${ADMIN_REFUND_PREFIX} | `))
  );
};

/** Тарифные платежи, которые админ может пометить возвратом (не пробный 1 ₽). */
export const isRefundablePayment = (payment) =>
  isSuccessfulCharge(payment) &&
  paymentAmountKopecks(payment) > TARIFF_PAYMENT_KOPECKS;

const paymentMonthKey = (payment, timeZone) => {
  const date = paymentMoment(payment);
  return date ? date.tz(timeZone).format("YYYY-MM") : null;
};

export const listCashYears = (payments, timeZone = "Europe/Moscow") => {
  const years = new Set([moment().tz(timeZone).year()]);
  payments.forEach((payment) => {
    const date = paymentMoment(payment);
    if (date) years.add(date.tz(timeZone).year());
  });
  return [...years].sort((a, b) => b - a);
};

/** Месяцы выбранного года, без будущих. */
export const listCashMonths = (
  year,
  timeZone = "Europe/Moscow",
  t = (source) => source,
) => {
  const now = moment().tz(timeZone);
  const lastIndex = Number(year) === now.year() ? now.month() : 11;
  return MONTH_LABELS.slice(0, lastIndex + 1).map((label, index) => ({
    value: String(index + 1).padStart(2, "0"),
    label: t(label),
  }));
};

const userLabel = (user, t) => {
  if (!user) return t("пользователь не найден");
  return user.name || user.full_name || user.email || `id ${user.id}`;
};

/**
 * Касса считается по сырому /payments/allPayments, а не только по живым
 * пользователям: иначе платежи удалённых аккаунтов пропадают из суммы.
 */
export const inspectMonthPayments = ({
  payments,
  users = [],
  year,
  month,
  timeZone = "Europe/Moscow",
  t = (source, params = {}) =>
    Object.entries(params).reduce(
      (result, [key, value]) =>
        result.replaceAll(`{{${key}}}`, String(value)),
      source,
    ),
}) => {
  const key = `${year}-${month}`;
  const userById = new Map(
    users.map((user) => [String(user.id), user]),
  );

  const rows = payments.map((payment) => {
    const date = paymentMoment(payment);
    const monthKey = paymentMonthKey(payment, timeZone);
    const amountKopecks = paymentAmountKopecks(payment);
    const user = userById.get(String(payment.user_id));
    const inMonth = monthKey === key;
    let skipReason = "";
    if (!date) skipReason = t("нет даты");
    else if (!inMonth) skipReason = t("другой месяц");
    else if (!isSuccessfulCharge(payment)) {
      skipReason = t("статус {{status}}", {
        status: payment.status || t("пусто"),
      });
    } else if (amountKopecks === null) skipReason = t("сумма не число");

    return {
      id: payment.id,
      paymentId: payment.payment_id,
      userId: payment.user_id,
      userName: userLabel(user, t),
      userMissing: !user,
      dateLabel: date ? date.tz(timeZone).format("DD.MM.YYYY HH:mm") : "—",
      dateValue: date ? date.valueOf() : 0,
      status: payment.status || "—",
      amountRubles: amountKopecks === null ? null : amountKopecks / 100,
      inMonth,
      noDate: !date,
      skipReason,
    };
  });

  const monthRows = rows
    .filter((row) => row.inMonth)
    .sort((a, b) => a.dateValue - b.dateValue);
  const countedLatest = uniqueLatestPayments(monthRows).filter(
    (row) => isSuccessfulCharge(row) && row.amountRubles !== null,
  );
  const countedRowIds = new Set(
    countedLatest.map((row) => row.id ?? `${row.paymentId}-${row.dateValue}`),
  );
  const countedGroupKeys = new Set(
    countedLatest.map((row) => paymentGroupKey(row)),
  );
  const monthRowsWithFlags = monthRows.map((row) => {
    const rowKey = row.id ?? `${row.paymentId}-${row.dateValue}`;
    const counted = countedRowIds.has(rowKey);
    return {
      ...row,
      counted,
      skipReason: counted
        ? ""
        : countedGroupKeys.has(paymentGroupKey(row)) &&
            isSuccessfulCharge(row)
          ? t("дубль вебхука")
          : row.skipReason,
    };
  });
  const undatedConfirmed = rows.filter(
    (row) => row.noDate && isSuccessfulCharge(row),
  );
  const counted = monthRowsWithFlags.filter((row) => row.counted);
  const skipped = monthRowsWithFlags.filter((row) => !row.counted);

  return {
    rows: monthRowsWithFlags,
    undatedConfirmed,
    counted,
    skipped,
    revenueRubles: countedLatest.reduce(
      (sum, row) => sum + (row.amountRubles || 0),
      0,
    ),
    confirmedCount: countedLatest.length,
    tariffCount: countedLatest.filter((row) => (row.amountRubles || 0) > 100)
      .length,
    trialCount: countedLatest.filter((row) => (row.amountRubles || 0) <= 100)
      .length,
    rejectedCount: monthRows.filter(
      (row) => String(row.status).toUpperCase() === "REJECTED",
    ).length,
  };
};

export const buildMonthCash = (params) => inspectMonthPayments(params);

export const buildAdminMetrics = ({
  users,
  groups,
  period,
  payments: rawPayments,
  timeZone = "Europe/Moscow",
}) => {
  const payments = Array.isArray(rawPayments)
    ? rawPayments
    : collectUserPayments(users);
  const periodPayments = uniqueLatestPayments(
    payments.filter((payment) => paymentIsInPeriod(payment, period, timeZone)),
  );
  const successfulPayments = periodPayments.filter(isSuccessfulCharge);
  const rejectedPayments = periodPayments.filter(isRejectedPayment);
  const revenueKopecks = successfulPayments.reduce((sum, payment) => {
    const amount = paymentAmountKopecks(payment);
    return sum + (amount || 0);
  }, 0);
  const activeTariffs = countTariffs(groups.active);
  const selectedTariffCount = users.filter(hasTariff).length;
  const everPaidCount = users.filter((user) =>
    uniqueLatestPayments(user.payments || []).some(isSuccessfulCharge),
  ).length;

  return {
    revenueRubles: revenueKopecks / 100,
    confirmedPayments: successfulPayments.length,
    rejectedPayments: rejectedPayments.length,
    mrrRubles:
      activeTariffs.monthly * MONTHLY_PRICE_RUBLES +
      activeTariffs.yearly * YEARLY_PRICE_PER_MONTH_RUBLES,
    activeTariffs,
    usersWithAccess: groups.active.length + groups.cancelled.length,
    selectedTariffCount,
    everPaidCount,
    referredCount: groups.referred.length,
    referredPaidCount: groups.referredPaid.length,
    totalUsers: users.length,
  };
};

export const getQueueUsers = (queue, users, groups) =>
  queue === "all" ? users : groups[queue] || [];

const normalizeReferralCode = (value) => String(value || "").trim().toLowerCase();

const referrerLabel = (user) => {
  if (!user) return "";
  return [user.name, user.full_name, user.email, user.id]
    .filter(Boolean)
    .join(" ");
};

/**
 * referred_by хранит код, а не id. Ищем владельца кода в той же выгрузке.
 * Если код есть, а человека нет — в таблице останется только код.
 */
export const attachReferrers = (users) => {
  const byCode = new Map();
  users.forEach((user) => {
    const code = normalizeReferralCode(user.referal_code);
    if (code && !byCode.has(code)) {
      byCode.set(code, user);
    }
  });

  return users.map((user) => {
    const code = normalizeReferralCode(user.referred_by);
    const referrer = code ? byCode.get(code) : null;
    if (!referrer || Number(referrer.id) === Number(user.id)) {
      return { ...user, referrer: null };
    }

    return {
      ...user,
      referrer: {
        id: referrer.id,
        name: referrer.name || referrer.full_name || "",
        email: referrer.email || "",
        referal_code: referrer.referal_code,
      },
    };
  });
};

export const filterAdminUsers = (users, searchValue) => {
  const query = searchValue.trim().toLowerCase();
  if (!query) return users;

  return users.filter((user) =>
    [
      user.id,
      user.name,
      user.full_name,
      user.email,
      user.phone_number,
      user.referal_code,
      user.referred_by,
      user.rebillid,
      referrerLabel(user.referrer),
    ].some((value) => String(value || "").toLowerCase().includes(query)),
  );
};
